import React from 'react';

import { InspectorHeader } from '@/components/ui/inspector/InspectorHeader';
import type { InspectorBreadcrumbItem } from '@/components/ui/inspector/types';
import { useBrandGuidelinesDialogStore } from '@/stores/useBrandGuidelinesDialogStore';

interface BrandGuidelinesBodyProps {
  brandKitId: string;
  onClose: () => void;
  breadcrumbs?: InspectorBreadcrumbItem[];
}

interface GuidelinesSection {
  title?: string;
  body?: string;
}

/**
 * Read-only brand guidelines viewer mounted by the Inspector when a brand
 * guidelines artifact is opened from /library. Reads payload from
 * useBrandGuidelinesDialogStore.
 *
 * Renders shape-tolerantly: brand_guidelines JSONB has no fixed schema. We
 * pull obvious fields (summary, sections, dos/donts) and fall back to a
 * formatted JSON dump so nothing is silently swallowed.
 */
export const BrandGuidelinesBody: React.FC<BrandGuidelinesBodyProps> = ({
  onClose,
  breadcrumbs,
}) => {
  const payload = useBrandGuidelinesDialogStore((s) => s.payload);
  if (!payload) return null;

  const { guidelines, brandKitName } = payload;
  const summary = (guidelines.summary as string | undefined) ?? '';
  const sections = (guidelines.sections as GuidelinesSection[] | undefined) ?? [];
  const dos = (guidelines.dos as string[] | undefined) ?? [];
  const donts = (guidelines.donts as string[] | undefined) ?? [];

  const hasStructured = summary || sections.length > 0 || dos.length > 0 || donts.length > 0;

  return (
    <div className="flex h-full flex-col">
      <InspectorHeader
        title={brandKitName}
        breadcrumbs={breadcrumbs}
        onClose={onClose}
        className="border-b border-border/40"
      />

      <div className="flex-1 overflow-y-auto px-5 py-6 space-y-6">
        {summary && (
          <section>
            <div className="text-detail uppercase tracking-wide text-muted-foreground">Summary</div>
            <p className="mt-1.5 text-sm text-foreground/90 leading-relaxed">{summary}</p>
          </section>
        )}

        {sections.map((s, i) => (
          <section key={i}>
            {s.title && (
              <div className="text-detail uppercase tracking-wide text-muted-foreground">
                {s.title}
              </div>
            )}
            {s.body && (
              <p className="mt-1.5 text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap">
                {s.body}
              </p>
            )}
          </section>
        ))}

        {dos.length > 0 && (
          <section>
            <div className="text-detail uppercase tracking-wide text-success">Do</div>
            <ul className="mt-1.5 space-y-1">
              {dos.map((d, i) => (
                <li key={i} className="text-sm text-foreground/85 leading-relaxed">
                  · {d}
                </li>
              ))}
            </ul>
          </section>
        )}

        {donts.length > 0 && (
          <section>
            <div className="text-detail uppercase tracking-wide text-destructive">Don&apos;t</div>
            <ul className="mt-1.5 space-y-1">
              {donts.map((d, i) => (
                <li key={i} className="text-sm text-foreground/85 leading-relaxed">
                  · {d}
                </li>
              ))}
            </ul>
          </section>
        )}

        {!hasStructured && (
          <pre className="text-detail text-muted-foreground bg-muted/40 p-3 rounded-md overflow-x-auto whitespace-pre-wrap">
            {JSON.stringify(guidelines, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
};
